import {
  RequestError,
  StatusError,
  type RequestEndpoint,
  type RequestOutput,
} from "./request.ts";

// Merge all properties: { a: 1, b: 2 } & { c: 3 } -> { a: 1, b: 2, c: 3 }
type Simplify<T> = { [K in keyof T]: T[K] } & {};

// Check if T can be an empty object
type CanBeEmptyObject<T> = [T] extends [object]
  ? keyof T extends never
    ? true
    : false
  : false;

// A subset of the QueryObserverResult type from React Query
interface QueryObserverResult<E extends RequestEndpoint> {
  // Will be a `RequestOutput<E>` or `undefined`, in this order of precedence:
  // - fresh data in the nominal case
  // - stale data when `staleTime` is passed (revalidation)
  // - previous data when queryKey has changed and `keepPreviousData` is true
  // - `placeholderData` if provided
  // - `undefined` in other cases
  data: RequestOutput<E> | undefined;
  // Will be a `RequestError` if the request failed on a network error or a stack-level server error (see HANDLED_CLIENT_ERROR_CODES and HANDLED_SERVER_ERROR_CODES)
  // Will be `null` in other cases (e.g. when the query is idle or when the query is skipped)
  error: RequestError | null;
  // Will be `true` if there's no cached/placeholder data and no query attempt was finished yet.
  isPending: boolean;
  // Will be `true` whenever the `queryFn` is executing, which includes initial load as well as background refetch)
  isFetching: boolean;
  // Will be `true` if the data shown is the placeholder data.
  isPlaceholderData: boolean;
  // manually refetch the query
  refetch: Refetch<E>;
}

type Refetch<E extends RequestEndpoint> = () => Promise<QueryObserverResult<E>>;

export function createQueryResult<E extends RequestEndpoint>(
  idle: boolean,
  key: QueryKey<E>,
  result: QueryObserverResult<E>,
): QueryResult<E> {
  // Explicit skip: the arg is false
  if (idle)
    return { state: "idle", isFetching: false, refetch: () => void 0, key };
  // A cancelled request is NOT a failure — it has no verdict, and React Query
  // has a refetch in flight (or the observer that cancelled it is already gone).
  // Reporting it as `state: "error"` let a request aborted by a component
  // remount masquerade as a real error: the Firebase auth-state remount during
  // signup cancels the pending room-invite query, and its cached
  // RequestAbortError then read as "invalid invite", dropping a valid pending
  // invite so invited users landed on the B2C exit instead of their room. Fall
  // through to pending/success so consumers see "still resolving", never
  // "failed".
  if (result.error && !result.error.isCancellation) {
    const status =
      result.error instanceof StatusError ? result.error.responseStatus : null;
    return {
      state: "error",
      isFetching: result.isFetching,
      message: result.error.message,
      status,
      error: result.error,
      refetch: result.refetch,
      key,
    };
  }
  if (result.data === undefined) {
    // Actually loading (fetch in progress)
    return {
      state: "pending",
      isFetching: result.isFetching,
      refetch: result.refetch,
      key,
    };
  }
  return {
    state: "success",
    isFetching: result.isFetching,
    isPlaceholderData: result.isPlaceholderData,
    refetch: result.refetch,
    key,
    ...result.data,
  };
}

// ActiveQueryResult: returned when the query arg cannot be false (always active).
// isPlaceholderData is true when:
// - the data is the provided placeholderData
// - keepPreviousData is true and the data is a previous QueryKey result
export type ActiveQueryResult<E extends RequestEndpoint> =
  | {
      state: "pending"; // initial load or QueryKey changed (actually fetching)
      isFetching: boolean;
      refetch: Refetch<E>;
      key: QueryKey<E>;
    }
  | {
      state: "error"; // the request failed on a network error or a stack-level server/client error (see HANDLED_CLIENT_ERROR_CODES and HANDLED_SERVER_ERROR_CODES)
      isFetching: boolean;
      message: string; // error message
      status: number | null; // HTTP status when available, otherwise null (e.g. network errors)
      error: RequestError; // the original RequestError for advanced handling/debugging
      refetch: Refetch<E>;
      key: QueryKey<E>;
    }
  | Simplify<
      {
        state: "success";
        isFetching: boolean;
        isPlaceholderData: boolean;
        refetch: Refetch<E>;
        key: QueryKey<E>;
      } & RequestOutput<E>
    >;

// QueryResult: returned when the query arg can be false (idle possible).
// state: "idle" = query disabled (arg is false), "pending" = initial load,
//        "error" = request failed, "success" = data available
export type QueryResult<E extends RequestEndpoint> =
  | {
      state: "idle"; // query disabled (arg is false)
      isFetching: false;
      refetch: () => void;
      key: QueryKey<E>;
    }
  | ActiveQueryResult<E>;

interface QueryOptions<E extends RequestEndpoint> {
  // the freshness duration in milliseconds of the data
  // a refetch is triggered on useQuery mounts if the data is stale
  // default is 0, which means the data is always refetched on useQuery mount
  staleTime?: number;
  // if true, staleTime is set to 1 hour (data is kept fresh longer, fewer refetches)
  // default is false
  durable?: boolean;
  // pass old query result in data when the QueryKey has changed (while the queryFn is fetching)
  // default is false
  keepPreviousData?: boolean;
  // the data to return on initial load or when the input key changes and with keepPreviousData is false
  placeholderData?: RequestOutput<E> extends Function
    ? never
    : RequestOutput<E>;
  // Automatically refetch at an interval. The resolver can stop polling from
  // the latest typed response without duplicating server state in React.
  refetchInterval?:
    number | false | ((data: RequestOutput<E> | undefined) => number | false);
  // Send the site's cookies with the request. The shared client omits them by
  // default; a route that reads one (the logged-out JuisciGPT visitor cookie)
  // opts in.
  credentials?: "include";
}

// ActiveQueryArg: the non-false input (always-active query)
export type ActiveQueryArg<E extends RequestEndpoint> = Simplify<
  E["input"] & QueryOptions<E>
>;

// QueryArg is the input of the RequestEndpoint and the query options
// false disables the query with `enabled: false` (see `useApiQuery` for why not `skipToken`)
type QueryArg<E extends RequestEndpoint> = ActiveQueryArg<E> | false;

// Variadic type to allow for optional arg if the input can be empty
export type QueryArgs<E extends RequestEndpoint> =
  CanBeEmptyObject<E["input"]> extends true
    ? [arg?: QueryArg<E>]
    : [arg: QueryArg<E>];

export type QueryKey<E extends RequestEndpoint> = ReturnType<
  typeof queryKey<E>
>;

type NestedPartial<T> = {
  [K in keyof T]?: T[K] extends object ? Partial<T[K]> : T[K];
};
export type QueryKeyInput<E extends RequestEndpoint> = NestedPartial<
  E["input"]
>;

// True when the endpoint requires a :param in its path (e.g. /admin/content/:id)
type HasParam<E extends RequestEndpoint> = E["input"] extends { param: infer P }
  ? P extends Record<string, unknown>
    ? [keyof P] extends [never]
      ? false
      : true
    : false
  : false;

// Variadic args for useApiRevalidate:
// - Parameterized routes → input required (pass {} for intentional broad invalidation)
// - Non-parameterized routes → input optional
export type RevalidateArgs<E extends RequestEndpoint> =
  HasParam<E> extends true
    ? [input: QueryKeyInput<E>]
    : [input?: QueryKeyInput<E>];

const emptyObject: Record<string, never> = Object.freeze({});

// Create a QueryKey from the path and input
// Used to identify the query in the cache and to invalidate the query
export function queryKey<E extends RequestEndpoint>(
  path: E["path"],
  input?: QueryKeyInput<E>,
) {
  const { param, query } = input === undefined ? {} : input;
  return [
    "api",
    path,
    param === undefined ? emptyObject : param,
    query === undefined ? emptyObject : query,
  ] as const;
}

// Infinite queries MUST live in a cache-key namespace DISTINCT from regular
// queries. react-query stores an infinite query's data as `{ pages, pageParams }`
// but a regular query's as the raw response — incompatible shapes. If the two
// ever shared a key, a persisted regular entry rehydrated under an infinite hook
// makes react-query dereference `data.pages[data.pages.length - 1]` on
// `undefined` and crash ("undefined is not an object (evaluating 't.length')")
// the instant the infinite hook mounts — a failure types cannot catch, because
// the bad data was minted by an earlier build. Appending this marker keeps the
// two shapes in separate cache slots (making that collision unrepresentable),
// while preserving the leading `["api", path, param, query]` prefix so that
// `queryKey`-based invalidation (useApiRevalidate) still matches both.
export const INFINITE_QUERY_MARKER = "infinite" as const;

export type InfiniteQueryKey<E extends RequestEndpoint> = ReturnType<
  typeof infiniteQueryKey<E>
>;

export function infiniteQueryKey<E extends RequestEndpoint>(
  path: E["path"],
  input?: QueryKeyInput<E>,
) {
  const [, , param, query] = queryKey<E>(path, input);
  return ["api", path, param, query, INFINITE_QUERY_MARKER] as const;
}

// --- Infinite Query ---

interface InfiniteEndpoint extends RequestEndpoint {
  output: {
    status: number;
    body: {
      docs: unknown[];
      meta?: { total?: number; limit?: number; offset?: number };
    };
    headers: Record<string, string>;
  };
}

export type InfiniteDoc<E extends InfiniteEndpoint> =
  RequestOutput<E>["body"]["docs"][number];

type OmitQueryOffset<I> = I extends {
  query: infer Q extends Record<string, unknown>;
}
  ? Omit<I, "query"> & { query: Omit<Q, "offset"> }
  : I;

interface InfiniteQueryOptions {
  staleTime?: number;
  durable?: boolean;
}

export type InfiniteQueryArg<E extends RequestEndpoint> =
  Simplify<OmitQueryOffset<E["input"]> & InfiniteQueryOptions> | false;

export type ActiveInfiniteQueryResult<E extends InfiniteEndpoint> =
  | { state: "pending"; isFetching: boolean; key: InfiniteQueryKey<E> }
  | {
      state: "error";
      isFetching: boolean;
      message: string;
      status: number | null;
      key: InfiniteQueryKey<E>;
    }
  | {
      state: "success";
      isFetching: boolean;
      docs: InfiniteDoc<E>[];
      /**
       * The page bodies, unflattened. `docs` is the concatenation of their
       * `docs`, which is what almost every caller wants — this is for the ones
       * whose endpoint serves a SIDE-CAR beside `docs` and has to join the two.
       *
       * `:uid/publications` is the case: it returns `majorDescriptorUis` next to
       * `docs` rather than folding MeSH into `populateArticles`, the shared
       * serializer of every content endpoint. A sidecar is per page, so flattened
       * docs alone cannot be joined to it.
       */
      bodies: RequestOutput<E>["body"][];
      total: number | undefined;
      hasNextPage: boolean;
      canFetchNextPage: boolean;
      /** A NEXT page is in flight, not a background refetch of the loaded ones. */
      isFetchingNextPage: boolean;
      fetchNextPage: () => void;
      key: InfiniteQueryKey<E>;
    };

export type InfiniteQueryResult<E extends InfiniteEndpoint> =
  | { state: "idle"; isFetching: false; key: InfiniteQueryKey<E> }
  | ActiveInfiniteQueryResult<E>;

interface InfiniteQueryObserverResult<E extends InfiniteEndpoint> {
  data: { pages: RequestOutput<E>[] } | undefined;
  error: RequestError | null;
  isPending: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

export function createInfiniteQueryResult<E extends InfiniteEndpoint>(
  idle: boolean,
  key: InfiniteQueryKey<E>,
  result: InfiniteQueryObserverResult<E>,
): InfiniteQueryResult<E> {
  if (idle) return { state: "idle", isFetching: false, key };
  // A cancelled request is not a failure (see createQueryResult) — never surface
  // an abort as `state: "error"`.
  if (result.error && !result.error.isCancellation) {
    const status =
      result.error instanceof StatusError ? result.error.responseStatus : null;
    return {
      state: "error",
      isFetching: result.isFetching,
      message: result.error.message,
      status,
      key,
    };
  }
  if (result.data === undefined)
    return { state: "pending", isFetching: result.isFetching, key };

  const pages = result.data.pages;
  const docs = pages.flatMap<InfiniteDoc<E>>((page) => page.body.docs);
  const firstPage = pages[0];
  const meta = firstPage === undefined ? undefined : firstPage.body.meta;
  const total = meta === undefined ? undefined : meta.total;

  return {
    state: "success",
    isFetching: result.isFetching,
    docs,
    bodies: pages.map((page) => page.body),
    total,
    hasNextPage: result.hasNextPage,
    canFetchNextPage: result.hasNextPage && !result.isFetching,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: result.fetchNextPage,
    key,
  };
}
